import { createTheme } from '@mui/material/styles';

/**
 * Brand colors. These hexes were already hand-written throughout custom.css and the
 * components (the green appears ~34 times); defining them here once means MUI components
 * inherit the brand instead of every component re-declaring it inline.
 */
const BRAND_GREEN = '#007A03';
const BRAND_GREEN_DARK = '#005502';
const BRAND_GREEN_LIGHT = '#2E9B31';

/**
 * Note: we deliberately do NOT use MUI's CssBaseline here. Bootstrap's reboot is already
 * loaded globally in index.js, and layering a second CSS reset on top of it restyles the
 * entire non-MUI half of the app.
 */
const theme = createTheme({
    palette: {
        primary: {
            main: BRAND_GREEN,
            dark: BRAND_GREEN_DARK,
            light: BRAND_GREEN_LIGHT,
            contrastText: '#ffffff',
        },
        text: {
            primary: '#2c3e50',
            secondary: '#7f8c8d',
        },
    },
    shape: {
        borderRadius: 8,
    },
    typography: {
        // matches the `body, input, select, textarea` rule in custom.css so MUI components
        // stop falling back to Roboto, which the app never loads
        fontFamily: '\'Source Sans Pro\', sans-serif',
        button: {
            textTransform: 'none',
            fontWeight: 600,
        },
    },
    components: {
        MuiDialog: {
            defaultProps: {
                maxWidth: 'sm',
                fullWidth: true,
                closeAfterTransition: true,
            },
            styleOverrides: {
                paper: {
                    borderRadius: 12,
                },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    fontWeight: 700,
                },
            },
        },
        MuiDialogActions: {
            styleOverrides: {
                root: {
                    padding: '16px 24px',
                    gap: 8,
                },
            },
        },
        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    paddingLeft: 20,
                    paddingRight: 20,
                },
            },
        },

        /**
         * The grids are most of the admin application and were the only major surface the theme did
         * not reach, so they were still rendering stock MUI defaults: a rule around every cell, flat
         * headers and no feedback on hover. Styling them here rather than per screen means the ten
         * screens that use a grid stay consistent with each other by construction.
         */
        MuiDataGrid: {
            defaultProps: {
                // Selection does nothing on any of these grids; all it produced was a row that
                // stayed highlighted after a click and looked like a state the user had entered.
                disableRowSelectionOnClick: true,
                density: 'comfortable',
                columnHeaderHeight: 48,
            },
            styleOverrides: {
                root: {
                    border: 'none',
                    // The page shell draws the frame, so the grid drawing its own doubles the line.
                    '--DataGrid-rowBorderColor': 'rgba(0, 0, 0, 0.06)',
                },
                columnHeaders: {
                    backgroundColor: '#f7f8f9',
                    borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                    borderRadius: 0,
                },
                columnHeaderTitle: {
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    color: '#5b6b7a',
                },
                columnSeparator: {
                    // The resize handles read as table rules on a grid this wide; resizing still
                    // works, the grab area is simply no longer drawn at rest.
                    opacity: 0,
                    '&:hover': {
                        opacity: 1,
                    },
                },
                cell: {
                    borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
                    // Vertical rules between every cell are what makes a grid read as a spreadsheet.
                    borderRight: 'none',
                    '&:focus, &:focus-within': {
                        // The default focus ring draws a blue box around a single cell, which looks
                        // like an error state on a grid nobody is editing.
                        outline: 'none',
                    },
                },
                columnHeader: {
                    '&:focus, &:focus-within': {
                        outline: 'none',
                    },
                },
                row: {
                    '&:hover': {
                        backgroundColor: 'rgba(0, 122, 3, 0.04)',
                    },
                    '&.Mui-selected': {
                        backgroundColor: 'rgba(0, 122, 3, 0.08)',
                        '&:hover': {
                            backgroundColor: 'rgba(0, 122, 3, 0.12)',
                        },
                    },
                },
                footerContainer: {
                    borderTop: '1px solid rgba(0, 0, 0, 0.08)',
                    minHeight: 48,
                },
                toolbarContainer: {
                    padding: '8px 8px 12px',
                    gap: 8,
                },
                overlay: {
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                },
            },
        },
    },
});

export default theme;